import { Dispatch, SetStateAction } from 'react'

import { useSOPsTable } from '../hooks/useSOPsTable'

import { Box } from '@chakra-ui/react'

import {
  TABLE_HEIGHT,
  TABLE_MAX_WIDTH,
  Table,
  TableBody,
  TablePagination,
  TableRow,
} from '@hivewatch/dark'

import { EditSOPModal, RemoveSOPModal } from '@/features/sop-crud'
import { StandardOperatingProceduresQuery } from '@/graphql/generated/operations'

import { RefetchSOPFnType } from '../types/types'
import { SOPsTableHeader } from './SOPsTableHeader'
import { SOPsTableRow } from './SOPsTableRow'
import { SOPsTableToolbar } from './SOPsTableToolbar'

const ROW_HEIGHT = 56
const HEADER_HEIGHT = 40
const PAGINATION_HEIGHT = 56
const TABS_HEIGHT = 69

interface SOPsTableIProps {
  sops?: StandardOperatingProceduresQuery
  refetch: RefetchSOPFnType
  onRowClick: (id: string) => void
  filter?: string
  setFilter?: (value: string) => void
  hiddenColumns?: string[]
  shouldHideToolbar?: boolean
  pageIndex: number
  setPageIndex: Dispatch<SetStateAction<number>>
  pageSize: number
  setPageSize: Dispatch<SetStateAction<number>>
  sortColumn: string | null
  setSortColumn: Dispatch<SetStateAction<string | null>>
  sortOrder: 'asc' | 'desc' | null
  setSortOrder: Dispatch<SetStateAction<'asc' | 'desc' | null>>
  isLoading?: boolean
  useMonoDate?: boolean
  useCompactLayout?: boolean
}

export const SOPsTable = ({
  sops,
  refetch,
  onRowClick,
  filter,
  setFilter,
  hiddenColumns,
  shouldHideToolbar = false,
  pageIndex,
  setPageIndex,
  pageSize,
  setPageSize,
  sortColumn,
  setSortColumn,
  sortOrder,
  setSortOrder,
  isLoading,
  useMonoDate,
  useCompactLayout,
}: SOPsTableIProps) => {
  const {
    colorScheme,
    columns,
    tableData,
    total,
    pageCount,
    sortableColumns,
    handleSortChange,
    handlePageSizeChange,
    sop,
    isEditSOPOpen,
    isRemoveSOPOpen,
    onEditSOPClose,
    onRemoveSOPClose,
  } = useSOPsTable({
    sops,
    hiddenColumns,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    sortColumn,
    setSortColumn,
    sortOrder,
    setSortOrder,
    useMonoDate,
    useCompactLayout,
  })

  const tableHeight = `min(calc(${TABLE_HEIGHT} - ${TABS_HEIGHT}px), ${
    HEADER_HEIGHT +
    (tableData.length || (isLoading ? pageSize : 1)) * ROW_HEIGHT +
    PAGINATION_HEIGHT
  }px)`

  return (
    <>
      {!shouldHideToolbar && (
        <SOPsTableToolbar
          colorScheme={colorScheme}
          filter={filter}
          setFilter={setFilter}
        />
      )}
      <Table
        colorScheme={colorScheme}
        height={tableHeight}
        maxWidth={TABLE_MAX_WIDTH}
        onSortChange={handleSortChange}
        sortableColumns={sortableColumns}
      >
        <SOPsTableHeader columns={columns} />
        <TableBody isLoading={isLoading} skeletonRowCount={pageSize}>
          {!isLoading && sops !== undefined && tableData.length === 0 ? (
            <TableRow>
              <Box
                alignItems='center'
                display='flex'
                flex='1'
                height='56px'
                justifyContent='center'
              >
                <Box color='text.secondary' fontSize='14px'>
                  List is empty
                </Box>
              </Box>
            </TableRow>
          ) : null}
          {tableData.map((row) => (
            <SOPsTableRow
              columns={columns}
              key={row.id}
              onClick={(row) => onRowClick(row.id)}
              row={row}
            />
          ))}
        </TableBody>
        <TablePagination
          onPageChange={setPageIndex}
          onPageSizeChange={handlePageSizeChange}
          pageCount={pageCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageSizeLabel='SOPs per page:'
          total={total}
        />
      </Table>
      {isRemoveSOPOpen && (
        <RemoveSOPModal
          isOpen={isRemoveSOPOpen}
          onClose={onRemoveSOPClose}
          refetch={refetch}
          sop={sop}
        />
      )}
      {isEditSOPOpen && (
        <EditSOPModal
          isOpen={isEditSOPOpen}
          onClose={onEditSOPClose}
          refetch={refetch}
          sop={sop}
        />
      )}
    </>
  )
}
